import axios from "@axios";

const DEFAULT_STATE = {
  brands: [],
  brandsTotal: 0,
  currentBrand: null,
  loading: false,
};

export default {
  namespaced: true,
  state: {
    ...DEFAULT_STATE,
  },
  getters: {
    getBrands: (state) => state.brands,
    getBrandsTotal: (state) => state.brandsTotal,
    getCurrentBrand: (state) => state.currentBrand,
    getLoading: (state) => state.loading,
  },
  mutations: {
    initState(state) {
      for (const [key, value] of Object.entries(DEFAULT_STATE)) {
        state[key] = value;
      }
    },
    setBrands(state, payload) {
      state.brands = payload;
    },
    setBrandsTotal(state, payload) {
      state.brandsTotal = payload;
    },
    setCurrentBrand(state, payload) {
      state.currentBrand = payload;
    },
    setLoading(state, payload) {
      state.loading = payload;
    },
  },
  actions: {
    initState({ commit }) {
      commit("initState");
    },
    async fetchBrands({ commit }, params = {}) {
      commit("setLoading", true);
      try {
        const { data } = await axios.get("/api/marketing/brands", { params });
        commit("setBrands", data.items);
        commit("setBrandsTotal", data.total);
        return data;
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchBrand({ commit }, brandId) {
      commit("setLoading", true);
      try {
        const { data } = await axios.get("/api/marketing/brands/" + brandId);
        commit("setCurrentBrand", data);
        return data;
      } finally {
        commit("setLoading", false);
      }
    },
    async createBrand({ dispatch }, brandData) {
      const { data } = await axios.post("/api/marketing/brands", brandData);
      return data;
    },
    async updateBrand({ dispatch }, { brandId, brandData }) {
      const { data } = await axios.patch(
        "/api/marketing/brands/" + brandId,
        brandData
      );
      return data;
    },
    async deleteBrand({ dispatch }, brandId) {
      await axios.delete("/api/marketing/brands/" + brandId);
    },
    async importBrands({ dispatch }, brands) {
      const { data } = await axios.post("/api/marketing/brands/import", {
        brands,
      });
      return data;
    },
    async exportBrands() {
      const { data } = await axios.get("/api/marketing/brands/export/json");
      return data;
    },
    async fetchBrandsForGeneration({ commit }) {
      commit("setLoading", true);
      try {
        const { data } = await axios.get("/api/marketing/brands/for-generation");
        return data;
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchPublicationLogs({ commit }, params = {}) {
      commit("setLoading", true);
      try {
        const { data } = await axios.get(
          "/api/marketing/brands/publication-logs",
          { params }
        );
        return data;
      } finally {
        commit("setLoading", false);
      }
    },
    async uploadImage({ commit }, { folder, file, filename, oldPath }) {
      const formData = new FormData();
      formData.append("file", file);
      var url = "/api/uploads/marketing/" + folder;
      var params = [];
      if (filename) {
        params.push("filename=" + encodeURIComponent(filename));
      }
      if (oldPath) {
        params.push("old_path=" + encodeURIComponent(oldPath));
      }
      if (params.length > 0) {
        url += "?" + params.join("&");
      }
      const { data } = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return data;
    },
    async deleteImage({ commit }, { folder, filename }) {
      await axios.delete("/api/uploads/marketing/" + folder + "/" + filename);
    },
    async uploadPresentation({ commit }, { file, filename, brandSlug }) {
      const formData = new FormData();
      formData.append("file", file);
      var url = "/api/uploads/marketing/presentations";
      const params = [];
      if (filename) params.push("filename=" + encodeURIComponent(filename));
      if (brandSlug) params.push("brand_slug=" + encodeURIComponent(brandSlug));
      if (params.length > 0) {
        url += "?" + params.join("&");
      }
      const { data } = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return data;
    },
    async convertToPdf({ commit }, { brandSlug, filename }) {
      const response = await axios.get(
        "/api/uploads/marketing/presentations/" +
          encodeURIComponent(brandSlug) +
          "/" +
          encodeURIComponent(filename) +
          "/pdf",
        {
          responseType: "blob",
        }
      );
      return response.data;
    },
    async uploadPdfOverride({ commit }, { brandSlug, pdfFr, pdfEn }) {
      const formData = new FormData();
      if (pdfFr) formData.append("pdf_fr", pdfFr);
      if (pdfEn) formData.append("pdf_en", pdfEn);
      const { data } = await axios.post(
        `/api/uploads/marketing/presentations/${encodeURIComponent(brandSlug)}/override-pdf`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data;
    },
    async uploadPptxOverride({ commit }, { brandSlug, pptxFr, pptxEn }) {
      const formData = new FormData();
      if (pptxFr) formData.append("pptx_fr", pptxFr);
      if (pptxEn) formData.append("pptx_en", pptxEn);
      const { data } = await axios.post(
        `/api/uploads/marketing/presentations/${encodeURIComponent(brandSlug)}/override-pptx`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data;
    },
    async deletePdfOverride({ commit }, { brandSlug, lang }) {
      await axios.delete(
        `/api/uploads/marketing/presentations/${encodeURIComponent(brandSlug)}/override-pdf`,
        { params: { lang } }
      );
    },
    async deletePptxOverride({ commit }, { brandSlug, lang }) {
      await axios.delete(
        `/api/uploads/marketing/presentations/${encodeURIComponent(brandSlug)}/override-pptx`,
        { params: { lang } }
      );
    },
    async publishBrand({ commit }, brandId) {
      const { data } = await axios.post(
        "/api/marketing/brands/" + brandId + "/publish"
      );
      return data;
    },
    async fetchSourceSettings() {
      const { data } = await axios.get("/api/marketing/source-settings");
      return data;
    },
    async updateSourceSettings({ commit }, settingsData) {
      const { data } = await axios.patch(
        "/api/marketing/source-settings",
        settingsData
      );
      return data;
    },
  },
};
